import { doc, getDoc } from "firebase/firestore";
import Hashids from "hashids";
import { db } from "./config";
import { env } from "../env";

export function hashUserId(userId: string | number): string {
  const secretKey = env.firebaseUserIdHashSalt;

  if (!userId) {
    throw new Error("User ID is required for hashing");
  }

  // Convert userId to number for Hashids
  const userIdNumber =
    typeof userId === "string" ? parseInt(userId, 10) : userId;

  // Create Hashids instance with secret salt
  const hashids = new Hashids(secretKey, 10);

  // Encode the user ID
  const hashId = hashids.encode(userIdNumber);

  return hashId;
}

export function decodeHashedUserId(hashedId: string): number {
  const secretKey = env.firebaseUserIdHashSalt;

  if (!hashedId) {
    throw new Error("Hashed ID is required for decoding");
  }

  // Create Hashids instance with same secret salt and settings
  const hashids = new Hashids(secretKey, 10);

  // Decode the hashed ID
  const decoded = hashids.decode(hashedId);

  if (!decoded || decoded.length === 0) {
    throw new Error("Invalid hashed ID or decoding failed");
  }

  // Return the first decoded number
  return decoded[0] as number;
}

/**
 * Whether this user may read `collectionName/documentId` and it exists — a
 * single-document read, so the Firestore rules check THIS doc's
 * `accessibleUserIds` against the signed-in uid (a query would be rejected
 * outright unless it filtered on `accessibleUserIds` itself). A missing doc
 * and a doc the user may not read both resolve `false`: the rules read
 * `resource.data`, so a missing doc is denied rather than reported absent.
 * @param userId - The current user's id (caller-supplied, e.g. from
 *   `useAuthStore.getState().user?.id`); only checked for presence — access
 *   itself is decided by the Firebase session (`auth-ready.ts`).
 * @param collectionName - The collection name
 * @param documentId - The document ID to check access for
 * @returns Promise<boolean> - true if access is granted, false otherwise
 */
export async function validateTrackingId(
  userId: string | number,
  collectionName: string,
  documentId?: string,
): Promise<boolean> {
  try {
    if (!userId) {
      throw new Error("User ID not found");
    }
    if (!documentId) {
      throw new Error("Document ID not provided");
    }

    const snapshot = await getDoc(doc(db, collectionName, documentId));
    if (snapshot.exists()) {
      return true;
    }
    throw new Error("Document doesn't exists");
  } catch (error) {
    console.error("[firebase] validateTrackingId failed:", error);
    return false;
  }
}
